import { Box, Button, Dialog, Grid, MenuItem, Stack, SxProps, TextField, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticTimePicker as MUITimePicker, StaticTimePickerProps as MUITimePickerProps } from '@mui/x-date-pickers/StaticTimePicker';
import { format, isValid } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import enLocale from 'date-fns/locale/en-US';
import frLocale from 'date-fns/locale/fr-CA';
import useTimezone from 'hooks/useTimezone';
import React, { useCallback, useMemo, useState } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

export type TimePickerProps = Omit<MUITimePickerProps<any>, 'value' | 'onChange' | 'slotProps'> & {
  name: string;
  required?: boolean;
  isDate?: boolean;
  parseError?: (error: FieldError) => string;
  onChange?: (date: any, keyboardInputValue?: string | undefined) => void;
  parseDate?: (date: any) => string;
  helperText?: string;
  sx?: SxProps;
  timeAMPM?: string;
  label?: string;
};

const TimePicker = React.memo(
  ({ isDate, parseError, name, label, required, parseDate, helperText, sx, timeAMPM, ...rest }: TimePickerProps) => (
    <Controller
      name={name}
      render={(props: any) => (
        <Picker {...props} label={label} helperText={helperText} required={required} parseError={parseError} sx={sx} timeAMPM={timeAMPM} />
      )}
    />
  )
);

const Picker = ({ field: { onBlur, onChange, value }, fieldState: { error, invalid }, label, helperText, required, parseError, sx }) => {
  const { timezone } = useTimezone();
  const intl = useIntl();
  const dateValue = useMemo(() => (value ? utcToZonedTime(value, timezone) : null), [value, timezone]);

  const [open, setOpen] = useState(false);
  const [ampm, setAMPM] = useState<string | null>(dateValue ? format(dateValue, 'a') : 'AM');

  const handleChange = useCallback(
    (date: any) => {
      if (date) {
        const combined = date.valueOf();
        onChange(zonedTimeToUtc(combined, timezone));
      } else {
        onChange(null);
      }
    },
    [onChange, timezone]
  );

  const handleAmpmChange = useCallback(
    (e: any) => {
      if (!dateValue) {
        return;
      }
      const newTime = e.target.value === 'AM' ? dateValue.valueOf() - 12 * 60 * 60 * 1000 : dateValue.valueOf() + 12 * 60 * 60 * 1000;
      onChange(zonedTimeToUtc(newTime, timezone));
      setAMPM(e.target.value);
    },
    [dateValue, onChange, timezone]
  );

  const handleAccept = useCallback(
    (date: any) => {
      handleChange(date);
    },
    [handleChange]
  );

  const MyActionBar = useCallback(
    ({ onAccept }: any) => (
      <Stack direction={'row'} justifyContent={'space-evenly'} m={2}>
        <Grid item sm={6} width={100}>
          {intl.locale !== 'fr' && ampm && (
            <TextField fullWidth size="small" select onChange={handleAmpmChange} value={ampm} label="AM/PM">
              <MenuItem value="AM">
                <FormattedMessage defaultMessage={'AM'} />
              </MenuItem>
              <MenuItem value="PM">
                <FormattedMessage defaultMessage={'PM'} />
              </MenuItem>
            </TextField>
          )}
        </Grid>
        <Button
          onClick={(params) => {
            onAccept(params);
            setOpen(false);
          }}
        >
          {' '}
          OK{' '}
        </Button>
      </Stack>
    ),
    [ampm, handleAmpmChange, intl]
  );

  const timeValue = useMemo(
    () =>
      dateValue && isValid(dateValue)
        ? intl.locale === 'fr'
          ? `${format(dateValue, 'HH:mm')}`
          : `${format(dateValue, 'hh:mm')} ${ampm}`
        : '',
    [ampm, dateValue, intl]
  );

  return (
    <>
      <TextField
        value={timeValue}
        onClick={() => setOpen(true)}
        onChange={(e: any) => onChange(e.target.value)}
        label={`${label} ${required ? ' *' : ''}`}
        error={invalid}
        helperText={error ? (typeof parseError === 'function' ? parseError(error as any) : error.message) : helperText}
        inputProps={{ readOnly: true }}
        sx={sx}
      />

      <Dialog onClose={() => setOpen(false)} open={open}>
        <Box
          sx={{
            '& .MuiPickersLayout-root': {
              display: 'flex',
              flexDirection: 'column'
            },
            '& .MuiTimeClock-root': {
              flexDirection: 'column-reverse',
              justifyContent: 'center',
              alignItems: 'center',

              ' & .MuiClock-root': {
                marginTop: '35px'
              },

              '& .MuiPickersArrowSwitcher-root': {
                position: 'relative',
                left: 'inherit',
                right: 'inherit'
              }
            },
            '& .MuiClock-amButton, .MuiClock-pmButton': {
              display: 'none'
            }
          }}
        >
          <Typography variant="h4" textAlign="center" mt={2}>
            <FormattedMessage defaultMessage="Select Time" />
          </Typography>
          <LocalizationProvider adapterLocale={intl.locale === 'fr' ? frLocale : enLocale} dateAdapter={AdapterDateFns}>
            <MUITimePicker
              value={dateValue}
              onChange={handleChange}
              onAccept={handleAccept}
              displayStaticWrapperAs="desktop"
              openTo="hours"
              shouldDisableTime={(value, clockType) => {
                if (clockType === 'minutes') {
                  return value.getMinutes() % 5 !== 0; // Disable minutes not multiples of 5
                }
                return false;
              }}
              slotProps={{
                actionBar: {
                  actions: ['accept']
                }
              }}
              slots={{
                actionBar: MyActionBar
              }}
            />
          </LocalizationProvider>
        </Box>
      </Dialog>
    </>
  );
};

export default TimePicker;
